'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HeroWireframe from '@/components/HeroWireframe';
import PerfectFilter from '@/components/PerfectFilter';
import LazySection from '@/components/LazySection';
import FAQSection from '@/components/FAQSection';
import { ProofStats, RacingBand } from '@/components/FlyerSections';
import { galleryImages } from '@/data/galleryImages';
import { homeFaqs } from '@/data/faqData';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY } from '@/constants/typography';

// Skeleton loader component
const SectionLoader = () => (
  <div style={{ minHeight: '200px', background: '#f5f5f5', margin: '20px 0' }} />
);

// Load all components client-side for better initial performance
// Only above-fold hero and filter need SSR
const HomeElement7 = dynamic(() => import('@/components/HomeElement7'), {
  loading: () => <SectionLoader />,
  ssr: false,
});
const ChosenByBest = dynamic(() => import('@/components/ChosenByBest'), {
  loading: () => <SectionLoader />,
  ssr: false,
});
const ConfidencePanel = dynamic(() => import('@/components/ConfidencePanel'), {
  loading: () => <SectionLoader />,
  ssr: false,
});

// Lazy load below-fold components - load only when near viewport
const HomeElement1 = dynamic(() => import('@/components/HomeElement1'), { ssr: false, loading: () => null });
const VideoSection = dynamic(() => import('@/components/VideoSection'), { ssr: false, loading: () => null });
const HomeElement2 = dynamic(() => import('@/components/HomeElement2'), { ssr: false, loading: () => null });
const HomeElement3 = dynamic(() => import('@/components/HomeElement3'), { ssr: false, loading: () => null });
const HomeElement4 = dynamic(() => import('@/components/HomeElement4'), { ssr: false, loading: () => null });
const HomeElement5 = dynamic(() => import('@/components/HomeElement5'), { ssr: false, loading: () => null });
const HomeElement6 = dynamic(() => import('@/components/HomeElement6'), { ssr: false, loading: () => null });
const Gallery = dynamic(() => import('@/components/Gallery'), { ssr: false, loading: () => null });
const FinalCTA = dynamic(() => import('@/components/FinalCTA'), { ssr: false, loading: () => null });

const HomePage: React.FC = () => {
  const gallery = galleryImages;

  return (
    <HomeWrapper>
      <div className="container-0">
        <div className="home-edge-to-edge">
          <Header />
          <HeroWireframe />
        </div>

        <main>
        {/* Product Search Section with Full API Integration */}
        <FilterSection id="filter-results">
          <PerfectFilter />
        </FilterSection>

        <AboutFiltersSection>
          {/* The page's single H1 - the hero banner carries its message in images only */}
          <AboutFiltersHeading as="h1">USA-Made Washable Performance Air Filters</AboutFiltersHeading>
          <AboutFiltersText>
            Green Filter USA designs and builds high performance air filters for daily drivers, enthusiasts, and racers who want more from their engine than a disposable paper filter can deliver. Every filter is engineered for strong airflow, dependable filtration, and long-term value, and every part of the process happens right here in the United States.
          </AboutFiltersText>

          <FeatureBlock>
            <FeatureHeading>Made in USA Quality</FeatureHeading>
            <FeatureText>
              Every Green Filter air filter is manufactured in the USA, from the woven cotton media to the final quality inspection. Building domestically lets us control materials, construction, and airflow testing at every stage, so drivers get a consistent, high performance filter instead of an unknown import product. American manufacturing also means faster turnaround on custom and private label filter orders for shops, racing teams, and OEM partners.
            </FeatureText>
          </FeatureBlock>

          <FeatureBlock>
            <FeatureHeading>Washable, Reusable Performance</FeatureHeading>
            <FeatureText>
              Unlike disposable paper filters that get thrown away every few thousand miles, a Green Filter is built to be cleaned, re-oiled, and reinstalled again and again. Our multi-layer woven cotton media is designed to maintain strong airflow and filtration efficiency through repeated cleanings, which means fewer replacement purchases, less waste, and a filter that keeps performing season after season with basic routine maintenance.
            </FeatureText>
          </FeatureBlock>

          <FeatureBlock>
            <FeatureHeading>Universal &amp; Vehicle-Specific Fitment</FeatureHeading>
            <FeatureText>
              Whether you need a drop-in replacement filter engineered for your exact make and model or a universal cone, cylinder, round, or panel filter for a custom intake build, Green Filter offers fitment options for cars, trucks, motorcycles, and motorsports applications. Our filter finder tool above makes it easy to search by vehicle, part number, or exact dimensions so you can confirm the right filter before you buy.
            </FeatureText>
          </FeatureBlock>

          <FeatureBlock>
            <FeatureHeading>Backed by a Lifetime Warranty</FeatureHeading>
            <FeatureText>
              Green Filter stands behind its air filters with a lifetime warranty against defects in materials and workmanship. Because our filters are washable and reusable rather than disposable, that warranty reflects real confidence in long-term performance, not a one-time purchase. If you ever have a fitment, performance, or warranty question, our customer service team is available to help you find the right solution.
            </FeatureText>
          </FeatureBlock>
        </AboutFiltersSection>

        <HomeElement7 />
        <ChosenByBest />
        <ConfidencePanel />

        {/* Brand Ticker + Embedded Video */}
        <LazySection>
          <HomeElement1 />
        </LazySection>

        {/* Brochure: racing story */}
        <RacingBand />

        <LazySection rootMargin="400px">
          <VideoSection />
        </LazySection>

        <LazySection>
          <HomeElement2 />
        </LazySection>

        {/* Brochure: test results */}
        <ProofStats />

        <LazySection>
          <HomeElement3 />
        </LazySection>

        <LazySection>
          <HomeElement4 />
        </LazySection>

        {/* FAQ Section - SSR'd (no LazySection/dynamic import) so AI crawlers and
            search engines can read the Q&A text directly from server-rendered HTML */}
        <FAQSection faqs={homeFaqs} title="Frequently Asked Questions About Green High Performance Air Filters" />

        {/* Video Carousel - Heavy component with react-multi-carousel */}
        <LazySection rootMargin="600px">
          <HomeElement5 />
        </LazySection>

        <LazySection>
          <HomeElement6 />
        </LazySection>

        <LazySection>
          <Gallery gallery={gallery} />
        </LazySection>

        {/* Final CTA */}
        <LazySection>
          <FinalCTA />
        </LazySection>
        </main>
      </div>
      <Footer />
    </HomeWrapper>
  );
};

export default HomePage;

const HomeWrapper = styled.div`
  background-color: #fafafa;
`;

const FilterSection = styled.section`
  padding: 0 24px 48px;
  max-width: 1280px;
  /* A light overlap onto the hero banner, not the old -110px that dragged
     the card up over the baked-in CTA button. Kept small on desktop because
     the first banner has artwork right down to its bottom edge; the
     portrait mobile banners put their "Find your filter" button right at
     the bottom edge, so on mobile the card starts just below the banner. */
  margin: -20px auto 0;
  position: relative;
  z-index: 4;

  @media (max-width: 767px) {
    padding: 0 16px 32px;
    margin-top: -15px;
  }
`;

const AboutFiltersSection = styled.section`
  padding: 56px 24px;
  max-width: 1100px;
  margin: 0 auto;

  @media screen and (max-width: 767px) {
    padding: 40px 20px;
  }
`;

const AboutFiltersHeading = styled.h2`
  ${TYPOGRAPHY.h2}
  color: ${COLORS.textPrimary};
  margin: 0 0 12px;
`;

const AboutFiltersText = styled.p`
  ${TYPOGRAPHY.bodyLG}
  color: ${COLORS.textSecondary};
  margin: 0 0 40px;
  max-width: 760px;
`;

const FeatureBlock = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FeatureHeading = styled.h3`
  ${TYPOGRAPHY.h3}
  color: ${COLORS.brandGreenAccessible};
  margin: 0 0 8px;
`;

const FeatureText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  margin: 0;
`;
